/**
 * Tela de login — dark mode, identidade visual igual ao landing site
 * (envpulse.com.br). Após sucesso volta pra rota original (state.from)
 * ou pro dashboard.
 */

import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, Lock, LogIn, Mail } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/api/client";
import { Button } from "@/components/Button";
import { BrandSymbol, BrandWordmark } from "@/components/Brand";

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // O erro de login mora NA TELA, não num toast.
  //
  // Toast é aviso de passagem: aparece longe do formulário, some sozinho, e
  // some justamente enquanto a pessoa ainda está relendo o que digitou. Para
  // "senha incorreta" isso é o pior comportamento possível — o usuário fica
  // sem saber se errou a senha, se o servidor caiu, ou se o clique nem
  // registrou. Aqui ele fica embaixo do botão, junto da ação que o causou, e
  // só sai quando há uma nova tentativa.
  const [erro, setErro] = useState<string | null>(null);

  if (auth.user) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErro(null);
    try {
      await auth.login(email, password);
      const dest =
        (location.state as LocationState | null)?.from?.pathname ?? "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setErro(apiErrorMessage(err, "Falha no login"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-full overflow-hidden">
      {/* Background: foto pura (login-bg.jpg em web/public/) */}
      <div aria-hidden className="envpulse-bg pointer-events-none absolute inset-0 -z-20" />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Lockup centralizado com glow forte */}
          <div className="mb-10 flex flex-col items-center gap-5">
            <BrandSymbol className="h-16 w-16" glow />
            <div className="flex flex-col items-center gap-3">
              <BrandWordmark className="text-4xl" variant="light" />
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/[0.08] px-3 py-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-200">
                  Monitoramento inteligente
                </span>
              </div>
            </div>
          </div>

          {/* Card de form glassmorphism */}
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.025] p-7 backdrop-blur-xl md:p-8"
            style={{ boxShadow: "0 12px 60px -12px rgba(0,0,0,0.6)" }}
          >
            <h2 className="mb-1 text-xl font-semibold text-white">Entrar</h2>
            <p className="mb-7 text-sm text-slate-400 dark:text-slate-500">
              Acesse o portal com suas credenciais.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="label-dark">Email</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    className="input-dark pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@empresa.com.br"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="label-dark">Senha</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className="input-dark pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
                >
                  Esqueci minha senha
                </Link>
              </div>

              {erro && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-red-400/40
                             bg-red-500/10 px-3 py-2.5 text-sm text-red-200"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                  <span>{erro}</span>
                </div>
              )}

              <Button
                type="submit"
                loading={submitting}
                icon={!submitting ? <LogIn className="h-4 w-4" /> : undefined}
                className="w-full"
              >
                Entrar
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
              problemas pra acessar?{" "}
              <a
                href="mailto:suporte@envpulse.com.br"
                className="text-cyan-300 hover:text-cyan-200 hover:underline"
              >
                suporte@envpulse.com.br
              </a>
            </p>
          </div>

          {/* Footer LAIT */}
          <p className="mt-6 text-center text-[10px] text-slate-500 dark:text-slate-400">
            uma empresa{" "}
            <span className="font-semibold text-slate-300">LAIT</span>
            {" "}— Latin American Inovação e Tecnologia
          </p>
        </div>
      </div>
    </div>
  );
}
